import { Injectable, computed, inject, signal } from '@angular/core';
import { OpenApiClient } from '../api/openapi-client.service';
import { AuthSessionStore } from '../auth/auth-session.store';
import { AuthorizationService } from '../auth/authorization.service';
import { ApiClientError, normalizeApiError } from '../http/api-error';
import {
  BranchContextOption,
  DepartmentContextOption,
  OrganizationContextSelection,
} from './organization-context.models';

const PAGE_SIZE = 100;

interface ContextIdentity {
  readonly userId: string;
  readonly sessionRevision: number;
}

interface ContextInitializeRequest {
  readonly generation: number;
  readonly promise: Promise<void>;
}

interface ContextCatalogEnvelope<T> {
  readonly data: readonly T[];
  readonly meta: { readonly last_page: number };
}

export interface ContextError {
  /** Translation key for the failure message. */
  readonly key: string;
  /** Correlation id to quote to support, when the server supplied one. */
  readonly supportId: string | null;
}

@Injectable({ providedIn: 'root' })
export class OrganizationContextService {
  private readonly api = inject(OpenApiClient);
  private readonly session = inject(AuthSessionStore);
  private readonly authorization = inject(AuthorizationService);
  private readonly branchesState = signal<readonly BranchContextOption[]>([]);
  private readonly departmentsState = signal<readonly DepartmentContextOption[]>([]);
  private readonly branchIdState = signal<string | null>(null);
  private readonly departmentIdState = signal<string | null>(null);
  private readonly loadingState = signal(false);
  private readonly loadedState = signal(false);
  private readonly errorState = signal<ContextError | null>(null);
  private readonly identityState = signal<ContextIdentity | null>(null);
  private generation = 0;
  private initializeRequest?: ContextInitializeRequest;

  private readonly belongsToCurrentSession = computed(() => {
    const identity = this.identityState();
    const user = this.session.user();

    return Boolean(
      identity &&
      user &&
      identity.userId === user.id &&
      identity.sessionRevision === this.session.revision(),
    );
  });

  readonly branches = computed(() =>
    this.belongsToCurrentSession() ? this.branchesState() : [],
  );
  readonly departments = computed(() =>
    this.belongsToCurrentSession() ? this.departmentsState() : [],
  );
  readonly branchId = computed(() =>
    this.belongsToCurrentSession() ? this.branchIdState() : null,
  );
  readonly departmentId = computed(() =>
    this.belongsToCurrentSession() ? this.departmentIdState() : null,
  );
  readonly loading = computed(() =>
    this.belongsToCurrentSession() && this.loadingState(),
  );
  readonly loaded = computed(() =>
    this.belongsToCurrentSession() && this.loadedState(),
  );
  readonly error = computed(() =>
    this.belongsToCurrentSession() ? this.errorState() : null,
  );
  readonly canViewAll = computed(() => this.authorization.has('organization.view-all'));
  readonly availableDepartments = computed(() => {
    const branchId = this.branchId();

    return branchId
      ? this.departments().filter((department) => department.branch_id === branchId)
      : this.departments();
  });
  readonly activeBranch = computed(
    () => this.branches().find((branch) => branch.id === this.branchId()) ?? null,
  );
  readonly activeDepartment = computed(
    () => this.departments().find((department) => department.id === this.departmentId()) ?? null,
  );
  readonly selection = computed<OrganizationContextSelection>(() => ({
    branchId: this.branchId(),
    departmentId: this.departmentId(),
  }));
  readonly queryParams = computed<Readonly<Record<string, string>>>(() => ({
    ...(this.branchId() ? { branch_id: this.branchId()! } : {}),
    ...(this.departmentId() ? { department_id: this.departmentId()! } : {}),
  }));

  initialize(): Promise<void> {
    const user = this.session.user();

    if (!user) {
      this.reset();
      return Promise.resolve();
    }

    const identity: ContextIdentity = {
      userId: user.id,
      sessionRevision: this.session.revision(),
    };

    if (!this.sameIdentity(this.identityState(), identity)) {
      this.beginIdentity(identity);
    }

    if (this.loadedState()) {
      return Promise.resolve();
    }

    if (this.initializeRequest?.generation === this.generation) {
      return this.initializeRequest.promise;
    }

    this.loadingState.set(true);
    this.loadedState.set(false);
    this.errorState.set(null);

    const generation = this.generation;
    const promise = this.load(identity, generation)
      .finally(() => {
        if (!this.isCurrent(identity, generation)) {
          return;
        }

        this.loadingState.set(false);
        this.loadedState.set(true);
        if (this.initializeRequest?.generation === generation) {
          this.initializeRequest = undefined;
        }
      });
    this.initializeRequest = { generation, promise };

    return promise;
  }

  selectBranch(branchId: string | null): void {
    if (!this.belongsToCurrentSession()) {
      return;
    }

    const validBranchId = this.validBranchId(branchId);
    this.branchIdState.set(validBranchId);

    const department = this.departmentsState().find(
      (item) => item.id === this.departmentIdState(),
    );

    if (!validBranchId || (department && department.branch_id !== validBranchId)) {
      this.departmentIdState.set(null);
    }

    this.persist();
  }

  selectDepartment(departmentId: string | null): void {
    if (!this.belongsToCurrentSession()) {
      return;
    }

    const department = this.departmentsState().find((item) => item.id === departmentId);
    const parentBranchId = this.validBranchId(department?.branch_id ?? null);

    if (!department || !parentBranchId) {
      this.departmentIdState.set(null);
      this.persist();
      return;
    }

    this.branchIdState.set(parentBranchId);
    this.departmentIdState.set(department.id);
    this.persist();
  }

  clearSelection(): void {
    if (!this.belongsToCurrentSession()) {
      return;
    }

    this.branchIdState.set(null);
    this.departmentIdState.set(null);
    this.persist();
  }

  reset(): void {
    this.generation += 1;
    this.identityState.set(null);
    this.initializeRequest = undefined;
    this.clearState();
  }

  private beginIdentity(identity: ContextIdentity): void {
    this.generation += 1;
    this.identityState.set(identity);
    this.initializeRequest = undefined;
    this.clearState();
  }

  private clearState(): void {
    this.branchesState.set([]);
    this.departmentsState.set([]);
    this.branchIdState.set(null);
    this.departmentIdState.set(null);
    this.loadingState.set(false);
    this.loadedState.set(false);
    this.errorState.set(null);
  }

  private async load(identity: ContextIdentity, generation: number): Promise<void> {
    if (!this.isCurrent(identity, generation)) {
      return;
    }

    if (!this.authorization.has('organization.view')) {
      this.restoreSelection(identity.userId);
      return;
    }

    try {
      const [branches, departments] = await Promise.all([
        this.loadAll<BranchContextOption>(
          (page) => this.api.request('organizationStructure.branches', {
            query: { active: true, page, per_page: PAGE_SIZE },
          }),
          identity,
          generation,
        ),
        this.loadAll<DepartmentContextOption>(
          (page) => this.api.request('organizationStructure.departments', {
            query: { active: true, page, per_page: PAGE_SIZE },
          }),
          identity,
          generation,
        ),
      ]);

      if (!this.isCurrent(identity, generation)) {
        return;
      }

      this.branchesState.set(this.sortByName(branches.filter((branch) => branch.active)));
      this.departmentsState.set(this.sortByName(departments.filter((department) => department.active)));
      this.restoreSelection(identity.userId);
    } catch (error: unknown) {
      if (!this.isCurrent(identity, generation)) {
        return;
      }

      const apiError = normalizeApiError(error);
      this.branchesState.set([]);
      this.departmentsState.set([]);
      this.branchIdState.set(null);
      this.departmentIdState.set(null);
      this.errorState.set(this.contextError(apiError));
    }
  }

  private async loadAll<T>(
    requestPage: (page: number) => Promise<ContextCatalogEnvelope<T>>,
    identity: ContextIdentity,
    generation: number,
  ): Promise<readonly T[]> {
    if (!this.isCurrent(identity, generation)) {
      return [];
    }

    const records: T[] = [];
    let page = 1;
    let lastPage = 1;

    do {
      const response = await requestPage(page);

      if (!this.isCurrent(identity, generation)) {
        return [];
      }

      records.push(...response.data);
      lastPage = Number.isFinite(response.meta.last_page) ? response.meta.last_page : page;
      page += 1;
    } while (page <= lastPage);

    return records;
  }

  private sameIdentity(
    left: ContextIdentity | null,
    right: ContextIdentity,
  ): boolean {
    return Boolean(
      left &&
      left.userId === right.userId &&
      left.sessionRevision === right.sessionRevision,
    );
  }

  private isCurrent(identity: ContextIdentity, generation: number): boolean {
    if (generation !== this.generation || !this.sameIdentity(this.identityState(), identity)) {
      return false;
    }

    const user = this.session.user();

    return Boolean(
      user &&
      user.id === identity.userId &&
      this.session.revision() === identity.sessionRevision,
    );
  }

  private restoreSelection(userId: string): void {
    const saved = this.readSelection(userId);
    let branchId = this.validBranchId(saved?.branchId ?? null);
    let department = this.departmentsState().find((item) => item.id === saved?.departmentId);
    const departmentBranchId = this.validBranchId(department?.branch_id ?? null);

    if (!departmentBranchId || (branchId && departmentBranchId !== branchId)) {
      department = undefined;
    } else if (department) {
      branchId = departmentBranchId;
    }

    this.branchIdState.set(branchId);
    this.departmentIdState.set(department?.id ?? null);

    if (!saved && !this.canViewAll()) {
      if (this.branchesState().length === 1) {
        this.branchIdState.set(this.branchesState()[0].id);
      }
      if (this.availableDepartments().length === 1) {
        const onlyDepartment = this.availableDepartments()[0];
        const parentBranchId = this.validBranchId(onlyDepartment.branch_id);

        if (parentBranchId) {
          this.branchIdState.set(parentBranchId);
          this.departmentIdState.set(onlyDepartment.id);
        }
      }
    }

    this.persist();
  }

  private validBranchId(branchId: string | null): string | null {
    return branchId && this.branchesState().some((branch) => branch.id === branchId)
      ? branchId
      : null;
  }

  private sortByName<T extends { name: string }>(items: readonly T[]): readonly T[] {
    return [...items].sort((left, right) => left.name.localeCompare(right.name));
  }

  /**
   * The failure is recorded as a translation key plus its parameters rather
   * than a finished sentence. A service that returns English prose cannot be
   * re-rendered when the clinician switches language mid-shift, and injecting
   * the translation service here would put an i18n dependency in front of
   * every consumer of the session.
   */
  private contextError(error: ApiClientError): ContextError {
    return {
      key: 'context.refreshFailed',
      supportId: error.requestId ?? null,
    };
  }

  private storageKey(userId: string): string {
    return `nabd.organization-context.${userId}`;
  }

  private persist(): void {
    const identity = this.identityState();

    if (!identity || !this.belongsToCurrentSession() || typeof sessionStorage === 'undefined') {
      return;
    }

    try {
      sessionStorage.setItem(this.storageKey(identity.userId), JSON.stringify(this.selection()));
    } catch {
      // Context persistence is an optional convenience; authorization remains server-side.
    }
  }

  private readSelection(userId: string): OrganizationContextSelection | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }

    try {
      const raw = sessionStorage.getItem(this.storageKey(userId));

      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as Record<string, unknown>;

      return {
        branchId: typeof parsed['branchId'] === 'string' ? parsed['branchId'] : null,
        departmentId: typeof parsed['departmentId'] === 'string' ? parsed['departmentId'] : null,
      };
    } catch {
      return null;
    }
  }
}
